/* eslint sort-keys: 1 */

import {
    baseConfig,
    IMG_BASE,
    CDN_DOMAIN,
    IS_DOCKER_NON_DEV,
    SVC_ENDPOINT,
    IS_BROWSER,
} from '@com/core/common/baseConfig';
import { TMarketplaceConfig } from '@com/core/common/types/config';

const marketplaceConfig: TMarketplaceConfig & {
    assets: {
        images: {
            frenzyDealLock: string;
        };
        icons: {
            user_light: string;
            warranty: string;
        };
    };
} = {
    ...baseConfig,
    commonHeaders: {
        ...baseConfig.commonHeaders,
        'X-MP': 'noon',
    },
    regions: {
        ...baseConfig.regions,
        ae: {
            ...baseConfig.regions.ae,
            codLimit: 5000,
            consumerRights: '/consumer-rights',
            enabled: true,
            freeDeliveryCap: 100,
            paymentMethods: ['mastercard', 'visa', 'cod', 'amex'],
            supportEmail: 'care@noon.com',
            supportPhone: '800-38888',
            whatsapp: 'https://wa.me/97180038888?text=Hello',
            emiBankLogos: [
                'emirates-{{lang}}.png',
                'adcb.svg',
                'mashreq.svg',
                'fab.svg',
                'rak.svg',
                'emirates-islamic.svg',
                'mawarid.png',
                'samba.svg',
                'standard-chartered.svg',
            ],
        },
        eg: {
            ...baseConfig.regions.eg,
            codLimit: 5000,
            enabled: true,
            freeDeliveryCap: 100,
            paymentMethods: ['mastercard', 'visa', 'cod', 'amex'],
            supportEmail: 'egypt@noon.com',
            supportPhone: '16358',
            emiBankLogos: ['nbd.svg', 'cib.svg', 'alex.svg', 'banque-misr.svg'],
        },
        sa: {
            ...baseConfig.regions.sa,
            codLimit: 5000,
            enabled: true,
            freeDeliveryCap: 100,
            paymentMethods: ['mastercard', 'visa', 'cod', 'amex', 'mada'],
            supportEmail: 'care@noon.com',
            supportPhone: '800-1160210',
            whatsapp: 'https://wa.me/97180038888?text=Hello',
            emiBankLogos: [
                'alvival.png',
                `emirates-{{lang}}.png`,
                'alrajhi.svg',
                'sabb.svg',
                'aljazira.png',
                'saib.svg',
            ],
        },
    },
    urls: {
        ...baseConfig.urls,
        appStore: 'https://itunes.apple.com/app/noon-%D9%86%D9%88%D9%86/id1269038866?ls=1&mt=8',
        facebook: 'https://facebook.com/noon/',
        googlePlay: 'https://play.google.com/store/apps/details?id=com.noon.buyerapp',
        imgBase: `${baseConfig.urls.imgBase}/noon/`,
        instagram: 'https://www.instagram.com/noon/',
        linkedin: 'https://www.linkedin.com/company/18045372/',
        supportInfo: {
            careers: 'https://www.jobsatnoon.com/',
            partners: `https://login.noon.partners/en/register`,
            privacyPolicy: '/privacy-policy',
            termsOfSale: '/terms-of-sale',
            termsOfUse: '/terms-of-use',
            warrantyPolicy: '/warranty-policy',
        },
        twitter: 'https://twitter.com/noon/',
    },
    // eslint-disable-next-line sort-keys
    endpoints: {
        ...baseConfig.endpoints,
        catalog:
            IS_DOCKER_NON_DEV && !IS_BROWSER
                ? 'http://catalog-noratelimit/api'
                : SVC_ENDPOINT.replace('/_svc', '/_svc/catalog/api'),
    },
    // eslint-disable-next-line sort-keys
    assets: {
        ...baseConfig.assets,
        icons: {
            ...baseConfig.assets.icons,
            address: `${IMG_BASE}/noon/icons/account_addresses.svg`,
            arrowDiagonal: `${IMG_BASE}/noon/icons/arrowDiagonal.svg`,
            back: `${IMG_BASE}/noon/icons/back.svg`,
            backArrow: '',
            caretDown: '',
            cart: `${IMG_BASE}/noon/icons/cart.svg`,
            cartBlack: `${IMG_BASE}/noon/icons/cart.svg`, // Added temp
            cartBrand: `${IMG_BASE}/noon/icons/cart-brand.svg`,
            cartWhite: '',
            check: ``,
            checkboxChecked: `${IMG_BASE}/noon/icons/checkbox_checked.svg`,
            checkboxSquareChecked: `${IMG_BASE}/noon/icons/checkbox-square_checked_v2.svg`,
            checkboxSquareUnchecked: `${IMG_BASE}/noon/icons/checkbox-square_unchecked.svg`,
            checkboxUnchecked: `${IMG_BASE}/noon/icons/checkbox_unchecked.svg`,
            chevronDown: `${IMG_BASE}/noon/icons/down-chevron.svg`,
            chevronLeft: `${IMG_BASE}/noon/icons/left-chevron.svg`,
            chevronRight: `${IMG_BASE}/noon/icons/right-chevron.svg`,
            chevronUp: `${IMG_BASE}/noon/icons/up-chevron.svg`,
            claims: `${IMG_BASE}/noon/icons/account_claims.svg`,
            closeXBlack: `${IMG_BASE}/noon/icons/close.svg`,
            closeXWhite: ``,
            collectionPointsToggle: `${IMG_BASE}/noon/icons/locker_switch.svg`,
            coupon: '',
            credits: `${IMG_BASE}/noon/icons/account_credits.svg`,
            edit: `${IMG_BASE}/noon/icons/edit.svg`,
            favicon: `${IMG_BASE}/noon/icons/favicon.ico`,
            filter: `${IMG_BASE}/noon/icons/filter.svg`,
            flame: `${IMG_BASE}/noon/icons/flame.svg`,
            freebieLocked: `${IMG_BASE}/noon/icons/freebie-lock-yellow.svg`,
            freebieUnlocked: `${IMG_BASE}/noon/icons/freebie-unlock-yellow.svg`,
            gift: `${IMG_BASE}/noon/icons/gift.svg`,
            globe: `${IMG_BASE}/noon/icons/globe.svg`,
            grid: `${IMG_BASE}/noon/icons/grid.svg`,
            hamburger: `${IMG_BASE}/noon/icons/hamburger.svg`,
            help: `${IMG_BASE}/noon/icons/help.svg`,
            home: `${IMG_BASE}/noon/icons/home.svg`,
            hourglass: `${IMG_BASE}/noon/icons/hourglass.svg`,
            info: ``,
            list: `${IMG_BASE}/noon/icons/list.svg`,
            magnifyingGlass: `${IMG_BASE}/noon/icons/magnifying_glass.svg`,
            minus: '',
            nextDayDelivery: `${IMG_BASE}/noon/icons/same_day_delivery.svg`,
            noonPay: `${IMG_BASE}/noon/icons/noon-pay.svg`,
            orders: `${IMG_BASE}/noon/icons/account_orders.svg`,
            payments: `${IMG_BASE}/noon/icons/account_payments.svg`,
            plus: '',
            preferences: `${IMG_BASE}/noon/icons/account_preferences.svg`,
            prepaid: `${IMG_BASE}/noon/icons/prepaid_only_items.svg`,
            prepaidOnly: `${IMG_BASE}/noon/icons/prepaid.svg`,
            profile: `${IMG_BASE}/noon/icons/account_profile.svg`,
            returns: `${IMG_BASE}/noon/icons/account_returns.svg`,
            returnsUsp: `${IMG_BASE}/noon/icons/returns-usp.svg`,
            search: `${IMG_BASE}/noon/icons/search.svg`,
            searchClear: `${IMG_BASE}/noon/icons/clear-search.svg`,
            share: `${IMG_BASE}/noon/icons/share.svg`,
            shipment: `${IMG_BASE}/noon/icons/shipment.svg`,
            shipmentMethod: {
                dd: `${IMG_BASE}/noon/icons/shipment_method_dd.svg`,
                fallback: `${IMG_BASE}/noon/icons/shipment_method_fallback.svg`,
                sc: `${IMG_BASE}/noon/icons/shipment_method_sc.svg`,
                sdd: `${IMG_BASE}/noon/icons/shipment_method_sdd.svg`,
            },
            shop: `${IMG_BASE}/noon/icons/shop.svg`,
            signOut: `${IMG_BASE}/noon/icons/sign_out.svg`,
            sort: `${IMG_BASE}/noon/icons/sort.svg`,
            trash: `${IMG_BASE}/noon/icons/trash.svg`,
            user: `${IMG_BASE}/noon/icons/user.svg`,
            user_light: `${IMG_BASE}/noon/icons/user_thin.svg`,
            warranty: `${IMG_BASE}/noon/icons/warranty.svg`,
            warrantyShield: `${IMG_BASE}/noon/icons/warranty-shield.svg`,
            wishList: `${IMG_BASE}/noon/icons/wishlist.svg`,
            wishListAdded: `${IMG_BASE}/noon/icons/wishlist-added.svg`,
            wishListBlack: `${IMG_BASE}/noon/icons/wishlist.svg`, // Added Temp
        },
        images: {
            ...baseConfig.assets.images,
            addressCollectionIndicator: `${IMG_BASE}/noon/images/address_collection_indicator.svg`,
            addressLatLngIndicator: `${IMG_BASE}/noon/images/address_latlng_indicator.svg`,
            addressesEmptySlate: `${IMG_BASE}/noon/images/account/addresses-empty.svg`,
            bankTransferEmpty: `${IMG_BASE}/noon/images/account/bank_transfer_empty.svg`,
            brandLoader: `${IMG_BASE}/noon/images/_loaders/noon-loader.gif`,
            cartEmpty: `${IMG_BASE}/noon/images/empty-state-cart.svg`,
            chevronRight: `${IMG_BASE}/noon/images/chevron_right.svg`,
            chevronRightWhite: `${IMG_BASE}/noon/images/chevron_right_white.svg`,
            claimAmountExceeded: `${IMG_BASE}/noon/images/account/claim_amount_exceeded.svg`,
            claimsEmptySlate: `${IMG_BASE}/noon/images/account/claims-empty.svg`,
            claimsSuccessSlate: `${IMG_BASE}/noon/images/account/claims-success.svg`,
            claimsTypeChecked: `${IMG_BASE}/noon/images/account/claim_type_checked.svg`,
            claimsTypeDamage: `${IMG_BASE}/noon/images/account/claim_damage_warranty.svg`,
            claimsTypeProtection: `${IMG_BASE}/noon/images/account/claim_protection.svg`,
            claimsTypeUnchecked: `${IMG_BASE}/noon/images/account/claim_type_unchecked.svg`,
            couponTag: `${IMG_BASE}/noon/images/coupon_tag.svg`,
            defaultMap: `${IMG_BASE}/noon/images/address_defualt_map.png`,
            defaultProductPlaceholder: `${CDN_DOMAIN}/nr/misc/product-image-default.jpg`,
            emptySearch: `${IMG_BASE}/noon/images/empty-state-search.svg`,
            error404: `${IMG_BASE}/noon/images/error-404.svg`,
            error429: `${IMG_BASE}/noon/images/error-429.svg`,
            error5xx: `${IMG_BASE}/noon/images/error-5xx.svg`,
            frenzyDealLock: `${IMG_BASE}/noon/images/frenzy-deal-lock.svg`,
            fulfilmentExpressAr: `${IMG_BASE}/noon/images/fulfilment_express-ar.png`,
            fulfilmentExpressEn: `${IMG_BASE}/noon/images/fulfilment_express-en.png`,
            fulfilmentMarketplaceAr: `${IMG_BASE}/noon/images/marketplace-ar.png`, // TODO: Update
            fulfilmentMarketplaceEn: `${IMG_BASE}/noon/images/marketplace-en.png`, // TODO: Update
            fulfilmentPreOrderAr: `${IMG_BASE}/noon/images/fulfilment_preorder-ar.svg`,
            fulfilmentPreOrderEn: `${IMG_BASE}/noon/images/fulfilment_preorder-en.svg`,
            logoBG: `${IMG_BASE}/noon/images/noon-logo-bg.svg`,
            loyalty: {
                bulletCheck: `${IMG_BASE}/noon/images/loyalty/vip-bullet-check.svg`,
                bulletStar: `${IMG_BASE}/noon/images/loyalty/vip-bullet-star.svg`,
                tick: `${IMG_BASE}/noon/images/loyalty/black-tick.svg`,
                vip: `${IMG_BASE}/noon/images/loyalty/vip.svg`,
                vipAR: `${IMG_BASE}/noon/images/loyalty/vip_en.svg?v=2`,
                vipEN: `${IMG_BASE}/noon/images/loyalty/vip_en.svg?v=2`,
            },
            mapCollectionPointLocker: `${IMG_BASE}/noon/images/map_collection-point-locker.svg`,
            mapCollectionPointPickup: `${IMG_BASE}/noon/images/map_collection-point-pickup.svg`,
            mapCollectionPointToggle: `${IMG_BASE}/noon/images/map_collection-point-locker.svg`,
            mapLandmarkPin: `${IMG_BASE}/noon/images/map_landmark-pin.svg`,
            mapLocateMeAr: `${IMG_BASE}/noon/images/map_locateme_ar.svg`,
            mapLocateMeEn: `${IMG_BASE}/noon/images/map_locateme_en.svg`,
            openGraphImage: 'https://k.nooncdn.com/s/misc/2017/opengraph-image-v2.jpg', // #TODO: Update
            paymentEmptySlate: `${IMG_BASE}/noon/images/account/payment-empty.svg`,
            returnSuccess: `${IMG_BASE}/noon/images/account/returns-success.svg`,
            returnablesEmptyState: `${IMG_BASE}/noon/images/account/returnables-empty.svg`,
            returnsEmptyState: `${IMG_BASE}/noon/images/account/returns-empty.svg`,
            secure: '',
            stores: {
                defaultBanner: `${IMG_BASE}/noon/images/store_header_default_banner.svg`,
                defaultLogo: `${IMG_BASE}/noon/images/store_default_logo.svg`,
            },
            toggleActive: `${IMG_BASE}/noon/images/account/toggle-active.svg`,
            toggleInactive: `${IMG_BASE}/noon/images/account/toggle-inactive.svg`,
            transferRequestSuccess: `${IMG_BASE}/noon/images/account/transfer_success.svg`,
        },
    },
    headerSupportBar: false,

    // eslint-disable-next-line sort-keys
    gtm: {
        ...baseConfig.gtm,
        // gaID: process.env.ENV === 'prod' ? 'UA-84507530-14' : 'UA-84507530-4',
        gaID: 'UA-84507530-14', // Super hot-fix
        gtmID: 'GTM-N6VX7WB',
        optimizeID: 'OPT-TLXWQP6',
    },

    // eslint-disable-next-line sort-keys
    enabledFeatures: {
        etracking: true,
        fingerprinting: false,
        loyalty: true,
    },

    etracking: {
        url: 'https://etracker.noon.com/track',
        id: 'noon',
    },
};

export default marketplaceConfig;
